<template>
  <section class="workspace-stack">
    <article class="glass-surface workspace-panel workspace-panel--hero">
      <div class="workspace-panel__header workspace-panel__header--spread">
        <div>
          <span class="workspace-kicker">Etapas</span>
          <h2 class="workspace-title">Gestion comercial de etapas</h2>
          <p class="workspace-copy">Define precios, stock, visibilidad y reglas de acceso desde una sola vista operativa.</p>
        </div>

        <div class="workspace-actions workspace-actions--compact">
          <button class="workspace-button workspace-button--primary" type="button" @click="openCreatePanel">
            <i class="fas fa-plus"></i>
            <span>Nueva etapa</span>
          </button>
        </div>
      </div>

      <div class="workspace-metrics">
        <article class="workspace-metric">
          <small>Total etapas</small>
          <strong>{{ tickets.length }}</strong>
        </article>
        <article class="workspace-metric">
          <small>Activas</small>
          <strong>{{ activeStages.length }}</strong>
        </article>
        <article class="workspace-metric">
          <small>Ocultas</small>
          <strong>{{ hiddenStages.length }}</strong>
        </article>
        <article class="workspace-metric">
          <small>Modo dominante</small>
          <strong>{{ modeHeadline }}</strong>
        </article>
      </div>
    </article>

    <article v-if="editorOpen" class="glass-surface workspace-panel workspace-editor-panel">
      <div class="workspace-panel__header workspace-panel__header--spread">
        <div>
          <span class="workspace-kicker">Editor</span>
          <h2 class="workspace-title">{{ editingTicketId ? 'Editar etapa' : 'Nueva etapa' }}</h2>
          <p class="workspace-copy">Los campos clave quedan visibles arriba. Lo menos frecuente vive dentro de opciones avanzadas.</p>
        </div>

        <button class="workspace-chip-button" type="button" @click="resetForm">
          <i class="fas fa-times"></i>
          <span>Cerrar</span>
        </button>
      </div>

      <form class="workspace-form workspace-form--two" @submit.prevent="submitStage">
        <label class="workspace-field">
          <span>Nombre</span>
          <input v-model="form.name" class="workspace-input" type="text" placeholder="General Early Bird" required />
        </label>

        <label class="workspace-field">
          <span>Precio</span>
          <input v-model.number="form.price" class="workspace-input" type="number" min="0" step="0.01" required />
        </label>

        <label class="workspace-field">
          <span>Stock</span>
          <input v-model.number="form.initial_quantity" class="workspace-input" type="number" min="1" required />
        </label>

        <label class="workspace-field">
          <span>Máximo por compra</span>
          <input v-model.number="form.max_quantity" class="workspace-input" type="number" min="1" required />
        </label>

        <label class="workspace-field workspace-field--wide">
          <span>Descripción</span>
          <textarea v-model="form.description" class="workspace-input workspace-input--textarea" rows="3" placeholder="Qué incluye y para quién aplica esta etapa"></textarea>
        </label>

        <label class="workspace-field workspace-field--wide">
          <span>Modo de códigos</span>
          <select v-model.number="form.code_mode" class="workspace-input workspace-select">
            <option :value="0">Sin códigos</option>
            <option :value="1">Aplica a todos los códigos</option>
            <option :value="2">Aplica solo a códigos seleccionados</option>
          </select>
        </label>

        <label v-if="form.code_mode === 2" class="workspace-field workspace-field--wide">
          <span>Códigos seleccionados</span>
          <select v-model="form.access_code_ids" class="workspace-input workspace-select workspace-select--multi" multiple>
            <option v-for="eventCode in activeCodes" :key="eventCode.id" :value="eventCode.id">{{ eventCode.code }} - {{ eventCode.label || 'Sin etiqueta' }}</option>
          </select>
          <small class="workspace-field__note">Una etapa puede compartir códigos con otras etapas sin perder su configuración de visibilidad.</small>
        </label>

        <div class="workspace-check-grid workspace-field--wide">
          <label class="workspace-inline-check">
            <input v-model="form.status" type="checkbox" />
            <span>Etapa activa</span>
          </label>
          <label class="workspace-inline-check">
            <input v-model="form.web_experience" type="checkbox" />
            <span>Visible en web</span>
          </label>
          <label class="workspace-inline-check">
            <input v-model="form.is_courtesy" type="checkbox" />
            <span>Cortesía</span>
          </label>
        </div>

        <div class="workspace-field workspace-field--wide workspace-advanced-block">
          <button class="workspace-chip-button workspace-chip-button--toggle" type="button" @click="advancedOpen = !advancedOpen">
            <i :class="advancedOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
            <span>Opciones avanzadas</span>
          </button>

          <div v-if="advancedOpen" class="workspace-advanced-grid">
            <label class="workspace-field">
              <span>Entradas por unidad</span>
              <input v-model.number="form.quantity" class="workspace-input" type="number" min="1" required />
            </label>

            <label class="workspace-field">
              <span>Tipo</span>
              <select v-model.number="form.type" class="workspace-input workspace-select">
                <option :value="1">Normal</option>
                <option :value="2">Discount</option>
                <option :value="3">Free</option>
                <option :value="4">Physical</option>
              </select>
            </label>

            <label class="workspace-field">
              <span>Categoría</span>
              <select v-model.number="form.category" class="workspace-input workspace-select">
                <option :value="1">FB / IG</option>
                <option :value="2">Tiktok</option>
                <option :value="3">Mail</option>
                <option :value="4">Ambassador</option>
                <option :value="5">Page</option>
              </select>
            </label>

            <label class="workspace-field">
              <span>Disponible desde</span>
              <input v-model="form.sales_date" class="workspace-input" type="datetime-local" />
            </label>

            <label class="workspace-field">
              <span>Disponible hasta</span>
              <input v-model="form.expiration_date" class="workspace-input" type="datetime-local" />
            </label>

            <div class="workspace-field workspace-field--wide workspace-limit-card">
              <div class="workspace-limit-card__header">
                <div>
                  <span class="workspace-limit-card__title">Límite por código en esta etapa</span>
                  <small class="workspace-field__note">Sirve para decidir cuántas veces puede entrar el mismo código a esta etapa. La home pública no consume este límite.</small>
                </div>
                <label class="workspace-inline-check workspace-inline-check--compact">
                  <input v-model="codeLimitEnabled" type="checkbox" :disabled="!codeUsageLimitApplies" />
                  <span>Limitar usos</span>
                </label>
              </div>

              <div class="workspace-limit-card__body">
                <label class="workspace-field workspace-field--limit-inline">
                  <span>Máximo de usos por código</span>
                <input
                  v-model.number="form.max_code_uses"
                  class="workspace-input"
                  type="number"
                  min="1"
                  :disabled="!codeUsageLimitApplies || !codeLimitEnabled"
                  placeholder="Ej: 3 usos"
                />
                </label>
                <p class="workspace-summary-line workspace-summary-line--compact">{{ codeLimitSummary }}</p>
              </div>
            </div>

            <p class="workspace-summary-line">{{ comboSummary }}</p>
          </div>
        </div>

        <div class="workspace-actions workspace-field--wide">
          <button class="workspace-button workspace-button--primary" type="submit" :disabled="saving">
            <i class="fas fa-save"></i>
            <span>{{ saving ? 'Guardando...' : editingTicketId ? 'Actualizar etapa' : 'Crear etapa' }}</span>
          </button>
          <button class="workspace-button" type="button" @click="resetForm">
            <i class="fas fa-rotate-left"></i>
            <span>{{ editingTicketId ? 'Cancelar edición' : 'Cancelar' }}</span>
          </button>
        </div>
      </form>
    </article>

    <p v-if="successMessage" class="workspace-feedback workspace-feedback--success">{{ successMessage }}</p>
    <p v-if="errorMessage" class="workspace-feedback workspace-feedback--error">{{ errorMessage }}</p>

    <article class="glass-surface workspace-panel">
      <div class="workspace-panel__header">
        <div>
          <span class="workspace-kicker">Inventario</span>
          <h2 class="workspace-title">Etapas actuales</h2>
        </div>
      </div>

      <div v-if="loading && !tickets.length" class="workspace-empty">Cargando etapas...</div>
      <div v-else-if="!tickets.length" class="workspace-empty">Todavía no hay etapas creadas. Crea la primera para empezar a vender o segmentar accesos.</div>
      <div v-else class="workspace-table-shell">
        <table class="workspace-table">
          <thead>
            <tr>
              <th>Etapa</th>
              <th>Precio</th>
              <th>Stock</th>
              <th>Max</th>
              <th>Combo</th>
              <th>Visibilidad</th>
              <th>Códigos</th>
              <th>Límite por código</th>
              <th>Status</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ticket in tickets" :key="ticket.id">
              <td>
                <div class="workspace-table-title">{{ ticket.name }}</div>
                <p class="workspace-table-subcopy">{{ ticket.description || 'Sin descripción.' }}</p>
              </td>
              <td>${{ formatMoney(ticket.price) }}</td>
              <td>{{ ticket.initial_quantity }}</td>
              <td>{{ ticket.max_quantity }}</td>
              <td>{{ comboLabel(ticket) }}</td>
              <td>{{ stageVisibilityLabel(ticket) }}</td>
              <td>
                <span v-if="Number(ticket.code_mode) === 1">Todos</span>
                <span v-else-if="Number(ticket.code_mode) === 0">Sin códigos</span>
                <span v-else>{{ accessCodeSummary(ticket) }}</span>
              </td>
              <td>{{ codeLimitLabel(ticket) }}</td>
              <td>
                <span :class="['workspace-status-pill', ticket.status ? 'is-active' : 'is-off']">{{ ticket.status ? 'Activa' : 'Oculta' }}</span>
              </td>
              <td>
                <div class="workspace-inline-actions">
                  <button class="workspace-chip-button" type="button" @click="editStage(ticket)">
                    <i class="fas fa-pen"></i>
                    <span>Editar</span>
                  </button>
                  <button class="workspace-chip-button" type="button" @click="deleteStage(ticket)">
                    <i class="fas fa-trash-alt"></i>
                    <span>{{ ticket.status ? 'Archivar' : 'Eliminar' }}</span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </article>
  </section>
</template>

<script>
import { apiService } from '../../common/api.services.js'

const defaultForm = () => ({
  name: '',
  description: '',
  initial_quantity: 100,
  price: 0,
  status: true,
  web_experience: true,
  is_courtesy: false,
  type: 1,
  category: 5,
  sales_date: '',
  expiration_date: '',
  require_ig: false,
  quantity: 1,
  max_quantity: 5,
  max_code_uses: null,
  code_mode: 0,
  access_code_ids: [],
})

export default {
  name: 'StagesView',

  props: {
    event: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      saving: false,
      tickets: [],
      codes: [],
      editingTicketId: null,
      codeLimitEnabled: false,
      successMessage: '',
      errorMessage: '',
      editorOpen: false,
      advancedOpen: false,
      form: defaultForm(),
    }
  },

  computed: {
    activeStages() {
      return this.tickets.filter((ticket) => ticket.status)
    },

    hiddenStages() {
      return this.tickets.filter((ticket) => !ticket.status)
    },

    activeCodes() {
      return this.codes.filter((code) => code.is_active)
    },

    modeHeadline() {
      const selectedCount = this.tickets.filter((ticket) => Number(ticket.code_mode) === 2).length
      const allCount = this.tickets.filter((ticket) => Number(ticket.code_mode) === 1).length

      if (selectedCount > 0) {
        return 'Seleccionados'
      }

      if (allCount > 0) {
        return 'Todos'
      }

        return 'Público'
    },

    comboSummary() {
      const units = Number(this.form.quantity) || 1
      const max = Number(this.form.max_quantity) || 1
      const total = units * max
      return units > 1
        ? `Cada unidad entrega ${units} entradas. El maximo por compra permite hasta ${total} accesos en una sola orden.`
        : `Cada unidad entrega una entrada. El maximo por compra permite hasta ${max} accesos en una sola orden.`
    },

    codeUsageLimitApplies() {
      return !this.form.web_experience && Number(this.form.code_mode) !== 0
    },

    codeLimitSummary() {
      if (!this.codeUsageLimitApplies) {
        return 'No aplica aquí: esta etapa está visible en home o no depende de códigos.'
      }

      if (!this.codeLimitEnabled || !this.form.max_code_uses) {
        return 'Cada código puede entrar sin límite a esta etapa privada.'
      }

      return `Un mismo código podrá entrar hasta ${this.form.max_code_uses} veces a esta etapa.`
    },
  },

  created() {
    this.fetchAll()
  },

  watch: {
    event: {
      deep: true,
      handler() {
        this.fetchAll()
      },
    },

    codeUsageLimitApplies(value) {
      if (!value) {
        this.codeLimitEnabled = false
        this.form.max_code_uses = null
      }
    },

    codeLimitEnabled(value) {
      if (!value) {
        this.form.max_code_uses = null
      }
    },
  },

  methods: {
    fetchAll() {
      if (!this.event || !this.event.id) {
        return
      }

      this.loading = true
      Promise.all([
        apiService(`/api/host/events/${this.event.id}/tickets/`),
        apiService(`/api/host/events/${this.event.id}/codes/`),
      ]).then(([tickets, codes]) => {
        this.tickets = Array.isArray(tickets) ? tickets : []
        this.codes = Array.isArray(codes) ? codes : []
      }).finally(() => {
        this.loading = false
      })
    },

    resetMessages() {
      this.successMessage = ''
      this.errorMessage = ''
    },

    openCreatePanel() {
      this.resetMessages()
      this.editingTicketId = null
      this.editorOpen = true
      this.advancedOpen = false
      this.codeLimitEnabled = false
      this.form = defaultForm()
    },

    formatMoney(value) {
      const parsed = Number.parseFloat(value)
      return Number.isNaN(parsed) ? '0.00' : parsed.toFixed(2)
    },

    formatInputDate(value) {
      if (!value) {
        return ''
      }

      const date = new Date(value)
      const pad = (number) => `${number}`.padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
    },

    stageVisibilityLabel(ticket) {
      if (ticket.web_experience && Number(ticket.code_mode) === 0) {
        return 'Web publica'
      }
      if (ticket.web_experience && Number(ticket.code_mode) === 1) {
        return 'Web + todos los códigos'
      }
      if (ticket.web_experience && Number(ticket.code_mode) === 2) {
        return 'Web + códigos elegidos'
      }
      if (Number(ticket.code_mode) === 1 || Number(ticket.code_mode) === 2) {
        return 'Privada por código'
      }
      return 'Oculta'
    },

    comboLabel(ticket) {
      const quantity = Number(ticket.quantity) || 1
      return quantity > 1 ? `${quantity}x por unidad` : 'Simple'
    },

    accessCodeSummary(ticket) {
      const count = Array.isArray(ticket.access_codes_detail) ? ticket.access_codes_detail.length : 0
      if (!count) {
        return 'Sin seleccion'
      }
      if (count === 1) {
        return ticket.access_codes_detail[0].code
      }
      return `${count} códigos`
    },

    codeLimitLabel(ticket) {
      if (ticket.web_experience || Number(ticket.code_mode) === 0) {
        return 'No aplica'
      }

      return ticket.max_code_uses ? `${ticket.max_code_uses} usos máx.` : 'Sin límite'
    },

    normalizedPayload() {
      const selectedCode = this.codes.find((code) => code.id === Number(this.form.access_code_ids[0]))
      return {
        ...this.form,
        code_mode: Number(this.form.code_mode),
        type: Number(this.form.type),
        category: Number(this.form.category),
        initial_quantity: Number(this.form.initial_quantity),
        price: Number(this.form.price),
        quantity: Number(this.form.quantity),
        max_quantity: Number(this.form.max_quantity),
        max_code_uses: this.codeUsageLimitApplies && this.codeLimitEnabled && this.form.max_code_uses ? Number(this.form.max_code_uses) : null,
        web_experience: Boolean(this.form.web_experience),
        require_ig: false,
        sales_date: this.form.sales_date ? `${this.form.sales_date}:00` : null,
        expiration_date: this.form.expiration_date ? `${this.form.expiration_date}:00` : null,
        access_code_ids: Number(this.form.code_mode) === 2 ? this.form.access_code_ids : [],
        code: Number(this.form.code_mode) === 2 && selectedCode ? selectedCode.code : '',
      }
    },

    submitStage() {
      this.resetMessages()
      this.saving = true

      const endpoint = this.editingTicketId
        ? `/api/host/events/tickets/${this.editingTicketId}/`
        : `/api/host/events/${this.event.id}/tickets/`

      apiService(endpoint, this.editingTicketId ? 'PATCH' : 'POST', this.normalizedPayload()).then((data) => {
        if (data && !data.err) {
          this.successMessage = this.editingTicketId ? 'Etapa actualizada.' : 'Etapa creada.'
          this.fetchAll()
          this.resetForm()
          return
        }

        this.errorMessage = (data && data.err) || 'No se pudo guardar la etapa.'
      }).finally(() => {
        this.saving = false
      })
    },

    editStage(ticket) {
      this.resetMessages()
      this.editingTicketId = ticket.id
      this.editorOpen = true
      this.advancedOpen = true
      this.codeLimitEnabled = Boolean(ticket.max_code_uses)
      this.form = {
        name: ticket.name || '',
        description: ticket.description || '',
        initial_quantity: ticket.initial_quantity || 0,
        price: Number.parseFloat(ticket.price) || 0,
        status: Boolean(ticket.status),
        web_experience: Boolean(ticket.web_experience),
        is_courtesy: Boolean(ticket.is_courtesy),
        type: Number(ticket.type) || 1,
        category: Number(ticket.category) || 5,
        sales_date: this.formatInputDate(ticket.sales_date),
        expiration_date: this.formatInputDate(ticket.expiration_date),
        require_ig: Boolean(ticket.require_ig),
        quantity: Number(ticket.quantity) || 1,
        max_quantity: Number(ticket.max_quantity) || 5,
        max_code_uses: Number(ticket.max_code_uses) || null,
        code_mode: Number(ticket.code_mode) || 0,
        access_code_ids: Array.isArray(ticket.access_codes_detail) ? ticket.access_codes_detail.map((code) => code.id) : [],
      }
    },

    resetForm() {
      this.editingTicketId = null
      this.editorOpen = false
      this.advancedOpen = false
      this.codeLimitEnabled = false
      this.form = defaultForm()
    },

    deleteStage(ticket) {
      this.resetMessages()
      apiService(`/api/host/events/tickets/${ticket.id}/`, 'DELETE').then((data) => {
        if (data && data.err) {
          this.errorMessage = data.err
          return
        }

        this.successMessage = ticket.status ? 'Etapa archivada.' : 'Etapa eliminada.'
        if (this.editingTicketId === ticket.id) {
          this.resetForm()
        }
        this.fetchAll()
      }).catch(() => {
        this.errorMessage = 'No se pudo eliminar la etapa.'
      })
    },
  },
}
</script>

<style scoped>
.workspace-panel--hero {
  display: grid;
  gap: 18px;
}

.workspace-panel__header--spread {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.workspace-editor-panel {
  display: grid;
  gap: 18px;
}

.workspace-advanced-block {
  display: grid;
  gap: 14px;
  padding: 16px;
  border: 1px solid rgba(223, 214, 255, 0.08);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.02);
}

.workspace-chip-button--toggle {
  justify-self: flex-start;
}

.workspace-advanced-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.workspace-summary-line {
  margin: 0;
  padding: 14px 16px;
  border-radius: 14px;
  color: var(--text-soft);
  background: rgba(203, 255, 232, 0.06);
  border: 1px solid rgba(203, 255, 232, 0.12);
  grid-column: 1 / -1;
}

.workspace-summary-line--compact {
  padding: 0;
  background: transparent;
  border: 0;
}

.workspace-limit-card {
  display: grid;
  gap: 12px;
  padding: 16px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(223, 214, 255, 0.08);
  grid-column: 1 / -1;
}

.workspace-limit-card__header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.workspace-limit-card__title {
  display: block;
  margin-bottom: 6px;
  color: var(--text-main);
  font-weight: 700;
}

.workspace-limit-card__body {
  display: grid;
  gap: 10px;
}

.workspace-field--limit-inline {
  display: grid;
  gap: 8px;
}

.workspace-inline-check--compact {
  white-space: nowrap;
}

.workspace-table-shell {
  overflow: auto;
}

.workspace-table {
  width: 100%;
  border-collapse: collapse;
}

.workspace-table th,
.workspace-table td {
  padding: 14px 12px;
  border-bottom: 1px solid rgba(223, 214, 255, 0.08);
  text-align: left;
  color: var(--text-soft);
  vertical-align: top;
}

.workspace-table th {
  color: var(--text-main);
  font-size: 0.75rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.workspace-table-title {
  color: var(--text-main);
  font-weight: 700;
}

.workspace-table-subcopy {
  margin: 6px 0 0;
  max-width: 340px;
}

.workspace-inline-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

@media (max-width: 900px) {
  .workspace-panel__header--spread {
    display: grid;
  }

  .workspace-advanced-grid {
    grid-template-columns: 1fr;
  }

  .workspace-limit-card__header {
    display: grid;
  }
}
</style>
